import {Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {translateName} from "../functions/language.functions";
import {
  BelegEntry,
  EigentumanteilMasterDetailEntryMeta,
  EigentumanteilVersionDetailEntryMeta,
  GbGemeindeListEntry,
  GbGrundstueckListEntryMeta,
  GlaeubigerMasterDetailEntryMeta,
  GrundstueckId,
  GrundstueckListEntry,
  HistInfo,
  LastRechtGrundstueckEntryMeta,
  LastRechtGruppeEntryMeta,
  PersonGbListEntryMeta,
  PersonGbMasterDetailEntryMeta,
  PersonStammMasterListEntryMeta,
  PersonStammVersionDetailEntryMeta,
  PfandrechtBelastungMasterDetailEntryMeta,
  RechteId,
  SchuldnerMasterDetailEntryMeta,
  TimePointListEntry,
} from "../../core/gb-api";

import {FormatConfigService} from "../../core/services/format-config.service";
import {
  AvBemerkungMasterDetailEntry,
  AvBemerkungVersionDetailEntry,
  AvMutation,
  BodenbedeckungMasterDetailEntry,
  BodenbedeckungVersionDetailEntry,
  CodeEntry,
  GbplanMasterDetailEntry,
  GebaeudeEingangAdresseListEntry as AvGebaeudeEingangAdresseListEntry,
  GebaeudeEingangAdresseMasterDetailEntryMeta,
  GebaeudeEingangAdresseVersionDetailEntryMeta,
  GebaeudeVersionDetailEntryMeta,
  GeometergeschaeftTimePoint,
  GrundstueckadresseDetailEntry,
  GrundstueckFlaechenanteilMasterDetailEntry,
} from "../../core/av-api";
import {GebaeudeEingangAdresseListEntry, GwrGrundstueckListEntry, Stockwerk} from "../../core/gwr-api";
import {LastRechtRootEntryVm} from "../../core/services/standardrecht-viewmodel.service";
import {RootBelastungVm} from "../../core/services/grundpfandrecht-viewmodel.service";
import {environment} from "../../../environments/environment";
import {AbstractRangregelungVm} from "app/core/services/rangregelung-viewmodel.service";

export interface PersonNameVorname {
  name?: string;
  vorname?: string;
}

@Injectable()
export class ComparatorService {

  constructor(private translateService: TranslateService,
              private config: FormatConfigService) {
  }

  private static getLagebezeichnungTypSortOrder(type: GrundstueckadresseDetailEntry.LagebezeichnungsTypEnum): number {
    switch (type) {
      case "STRASSE" :
        return 1;
      case "ORTSNAME":
        return 2;
      case "FLURNAME":
        return 3;
      default:
        return 4;
    }
  }

  // extracted from ngx-datatable
  public orderByComparator = (a: any, b: any): number => {
    if (a === null || typeof a === 'undefined') {
      a = 0;
    }
    if (b === null || typeof b === 'undefined') {
      b = 0;
    }
    if (a instanceof Date && b instanceof Date) {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
    } else if ((isNaN(parseFloat(a)) || !isFinite(a)) || (isNaN(parseFloat(b)) || !isFinite(b))) {
      // Convert to string in case of a=0 or b=0
      a = String(a);
      b = String(b);
      // Isn't a number so lowercase the string to properly compare
      if (a.toLowerCase() < b.toLowerCase()) {
        return -1;
      }
      if (a.toLowerCase() > b.toLowerCase()) {
        return 1;
      }
    } else {
      // Parse strings as numbers to compare properly
      if (parseFloat(a) < parseFloat(b)) {
        return -1;
      }
      if (parseFloat(a) > parseFloat(b)) {
        return 1;
      }
    }

    // equal each other
    return 0;
  }

  public grundstueckComparator = (a: GrundstueckListEntry | GbGrundstueckListEntryMeta, b: GrundstueckListEntry | GbGrundstueckListEntryMeta): number => {
    {
      const aGemeindeKey = ((a || {}).grundstueckNummer || {}).gbGemeinde;
      const bGemeindeKey = ((b || {}).grundstueckNummer || {}).gbGemeinde;
      const aGemeinde = translateName((aGemeindeKey || {}).name, this.translateService);
      const bGemeinde = translateName((bGemeindeKey || {}).name, this.translateService);

      if (aGemeinde === bGemeinde) {

        const aBfsNummer = (((a || {}).grundstueckNummer || {}).gbGemeinde || {}).bfsNummer;
        const bBfsNummer = (((b || {}).grundstueckNummer || {}).gbGemeinde || {}).bfsNummer;

        if (aBfsNummer === bBfsNummer) {

          const aKreisNummer = (((a || {}).grundstueckNummer || {}).gbGemeinde || {}).kreisNummer;
          const bKreisNummer = (((b || {}).grundstueckNummer || {}).gbGemeinde || {}).kreisNummer;

          if (aKreisNummer === bKreisNummer) {

            const aGrundstueckNummer = ((a || {}).grundstueckNummer || {}).nummer;
            const bGrundstueckNummer = ((b || {}).grundstueckNummer || {}).nummer;

            if (aGrundstueckNummer === bGrundstueckNummer) {
              const aNummerZusatz = ((a || {}).grundstueckNummer || {}).nummerZusatz;
              const bNummerZusatz = ((b || {}).grundstueckNummer || {}).nummerZusatz;
              const aNummer3 = ((a || {}).grundstueckNummer || {}).nummer3;
              const bNummer3 = ((b || {}).grundstueckNummer || {}).nummer3;
              const aNummer4 = ((a || {}).grundstueckNummer || {}).nummer4;
              const bNummer4 = ((b || {}).grundstueckNummer || {}).nummer4;

              if (aNummerZusatz === bNummerZusatz) {
                if (aNummer3 === bNummer3) {
                  if (aNummer4 === bNummer4) {
                    return this.erstellzeitpunktComparator(a, b);
                  }
                  return this.orderByComparator(aNummer4, bNummer4);
                }
                return this.orderByComparator(aNummer3, bNummer3);
              }
              return this.orderByComparator(aNummerZusatz, bNummerZusatz);
            }
            return this.orderByComparator(aGrundstueckNummer, bGrundstueckNummer);
          }
          return this.orderByComparator(aKreisNummer, bKreisNummer);
        }
        return this.orderByComparator(aBfsNummer, bBfsNummer);
      }
      return this.orderByComparator(aGemeinde, bGemeinde);
    }
  }

  public gwrGrundstueckComparator = (a: GwrGrundstueckListEntry, b: GwrGrundstueckListEntry): number => {
    const aGemeinde = (((a || {}).grundstueckNummer || {}).gemeinde || {}).name;
    const bGemeinde = (((b || {}).grundstueckNummer || {}).gemeinde || {}).name;

    if (aGemeinde === bGemeinde) {

      const aGrundstueckNummer = ((a || {}).grundstueckNummer || {}).nummer;
      const bGrundstueckNummer = ((b || {}).grundstueckNummer || {}).nummer;

      if (aGrundstueckNummer === bGrundstueckNummer) {
        const aNummerZusatz = ((a || {}).grundstueckNummer || {}).nummerZusatz;
        const bNummerZusatz = ((b || {}).grundstueckNummer || {}).nummerZusatz;
        const aNummer3 = ((a || {}).grundstueckNummer || {}).nummer3;
        const bNummer3 = ((b || {}).grundstueckNummer || {}).nummer3;
        const aNummer4 = ((a || {}).grundstueckNummer || {}).nummer4;
        const bNummer4 = ((b || {}).grundstueckNummer || {}).nummer4;

        if (aNummerZusatz === bNummerZusatz) {
          if (aNummer3 === bNummer3) {
            return this.orderByComparator(aNummer4, bNummer4);
          } else {
            return this.orderByComparator(aNummer3, bNummer3);
          }
        }
        return this.orderByComparator(aNummerZusatz, bNummerZusatz);
      }
      return this.orderByComparator(aGrundstueckNummer, bGrundstueckNummer);
    }
    return this.orderByComparator(aGemeinde, bGemeinde);
  }


  public timepointComparator = (histInfoA: HistInfo, histInfoB: HistInfo) => {
    const timePointA = histInfoA.vonTimePoint;
    const timePointB = histInfoB.vonTimePoint;
    if (!timePointA && !timePointB) {
      return 0;
    }
    if (!timePointA && timePointA.type === 'ERSTERFASSUNG_UEBERGEBEN') {
      return 1;
    }
    if (!timePointB && timePointB.type === 'ERSTERFASSUNG_UEBERGEBEN') {
      return -1;
    }
    return this.orderByComparator(timePointA.zeitpunktRechtsgueltig, timePointB.zeitpunktRechtsgueltig);
  }

  public belegComparator(belegA: BelegEntry, belegB: BelegEntry) {
    const a = belegA.geschaeftsnummer;
    const b = belegB.geschaeftsnummer;
    if (belegA.alt === belegB.alt) {
      if (a.amtNummer === b.amtNummer) {
        if (a.jahr === b.jahr) {
          if (a.nummer === b.nummer) {
            return this.orderByComparator(a.index, b.index);
          } else {
            return this.orderByComparator(a.nummer, b.nummer);
          }
        } else {
          return this.orderByComparator(a.jahr, b.jahr);
        }
      } else {
        return this.orderByComparator(a.amtNummer, b.amtNummer);
      }
    } else {
      return this.orderByComparator(belegA.alt, belegB.alt);
    }
  }

  public orderByGebaeudeComparator(prio1SortA: any, prio1SortB: any, gebaeudeA: GebaeudeVersionDetailEntryMeta, gebaeudeB: GebaeudeVersionDetailEntryMeta) {
    if (!prio1SortA && prio1SortB) {
      return 1;
    }
    if (prio1SortA && !prio1SortB) {
      return -1;
    }
    if (prio1SortA === prio1SortB) {
      if (gebaeudeA.gwrEgid === gebaeudeB.gwrEgid) {
        if (gebaeudeA.kantGid === gebaeudeB.kantGid) {
          return this.orderByComparator(gebaeudeA.versicherungsNummer, gebaeudeB.versicherungsNummer);
        }
        return this.orderByComparator(gebaeudeA.kantGid, gebaeudeB.kantGid);
      }
      return this.orderByComparator(gebaeudeA.gwrEgid, gebaeudeB.gwrEgid);
    } else {
      if (prio1SortA && prio1SortB) {
        return this.orderByComparator(prio1SortA, prio1SortB);
      }
    }
  }

  public rechtIdVersionComparator = (a: RechteId, b: RechteId,
                                     histInfoA: HistInfo, histInfoB: HistInfo, erstellzeitpunktA: Date, erstellzeitpunktB: Date) => {
    if (!a) {
      return -1;
    }
    if (!b) {
      return 1;
    }
    if (a.grundbuchamt.nummer === b.grundbuchamt.nummer) {
      if (a.rechteIdJahr === b.rechteIdJahr) {
        if (a.rechteIdNummer === b.rechteIdNummer) {
          if (erstellzeitpunktA || erstellzeitpunktB) {
            return this.orderByComparator(erstellzeitpunktA, erstellzeitpunktB);
          }
          return this.timepointComparator(histInfoA, histInfoB);
        } else {
          return this.orderByComparator(a.rechteIdNummer, b.rechteIdNummer);
        }
      } else {
        return this.orderByComparator(a.rechteIdJahr, b.rechteIdJahr);
      }
    } else {
      return this.orderByComparator(a.grundbuchamt.nummer, b.grundbuchamt.nummer);
    }
  }

  public rechtIdComparator(a: RechteId, b: RechteId) {
    if (!a && !b) {
      return 0;
    } else if (!a) {
      return -1;
    } else if (!b) {
      return 1;
    }
    if (a.grundbuchamt.nummer === b.grundbuchamt.nummer) {
      if (a.rechteIdJahr === b.rechteIdJahr) {
        return this.orderByComparator(a.rechteIdNummer, b.rechteIdNummer);
      } else {
        return this.orderByComparator(a.rechteIdJahr, b.rechteIdJahr);
      }
    } else {
      return this.orderByComparator(a.grundbuchamt.nummer, b.grundbuchamt.nummer);
    }
  }


  public translatedStringComparator = (a: string, b: string) => {
    let aTranslated = '';
    let bTranslated = '';
    if (a) {
      aTranslated = this.translateService.instant(a);
    }
    if (b) {
      bTranslated = this.translateService.instant(b);
    }
    // use swiss german locale. Sorting on french should be the same
    return aTranslated.localeCompare(bTranslated, 'de-CH');
  }

  public lastRechtComparator(a: LastRechtGruppeEntryMeta, b: LastRechtGruppeEntryMeta) {
    // Beide LastRechtGruppen haben genau eine Last -> Vergleich nach Last
    if (a.grundstueckeLast.length === 1 && b.grundstueckeLast.length === 1) {
      const lrA = a.grundstueckeLast[0];
      const lrB = b.grundstueckeLast[0];
      return this.grundstueckComparator(lrA.grundstueck, lrB.grundstueck);
    }
    if (a.grundstueckeLast.length === 1 && b.grundstueckeLast.length !== 1) {
      return -1;
    }
    if (a.grundstueckeLast.length !== 1 && b.grundstueckeLast.length === 1) {
      return 1;
    }
    if (a.grundstueckeLast.length !== 1 && b.grundstueckeLast.length !== 1) {
      // todo: compare by berechtigungen
      return 0;
    }
  }

  public geschaeftComparator(a: TimePointListEntry, b: TimePointListEntry) {
    if (a && a.geschaeftsnummer && b && b.geschaeftsnummer) {
      if (a.geschaeftsnummer.amtNummer === b.geschaeftsnummer.amtNummer) {
        if (a.geschaeftsnummer.jahr === b.geschaeftsnummer.jahr) {
          if (a.geschaeftsnummer.nummer === b.geschaeftsnummer.nummer) {
            if (a.geschaeftsnummer.index === b.geschaeftsnummer.index) {
              return this.orderByComparator(a.geschaeftsnummer.index, b.geschaeftsnummer.index);
            } else {
              return this.orderByComparator(a.geschaeftsnummer.index, b.geschaeftsnummer.index);
            }
          } else {
            return this.orderByComparator(a.geschaeftsnummer.nummer, b.geschaeftsnummer.nummer);
          }
        } else {
          return this.orderByComparator(a.geschaeftsnummer.jahr, b.geschaeftsnummer.jahr);
        }
      } else {
        return this.orderByComparator(a.geschaeftsnummer.amtNummer, b.geschaeftsnummer.amtNummer);
      }
    }
  }

  public geometergeschaeftComparator(a: GeometergeschaeftTimePoint, b: GeometergeschaeftTimePoint) {
    if (a && a.timePoint && a.timePoint.geschaeftsnummer && b && b.timePoint && b.timePoint.geschaeftsnummer) {
      if (a.namePolitischeGemeinde === b.namePolitischeGemeinde) {
        if (a.timePoint.geschaeftsnummer.jahr === b.timePoint.geschaeftsnummer.jahr) {
          if (a.timePoint.geschaeftsnummer.nummer === b.timePoint.geschaeftsnummer.nummer) {
            if (a.timePoint.geschaeftsnummer.index === b.timePoint.geschaeftsnummer.index) {
            }
            return this.orderByComparator(a.timePoint.geschaeftsnummer.index, b.timePoint.geschaeftsnummer.index);
          } else {
            return this.orderByComparator(a.timePoint.geschaeftsnummer.nummer, b.timePoint.geschaeftsnummer.nummer);
          }
        } else {
          return this.orderByComparator(a.timePoint.geschaeftsnummer.jahr, b.timePoint.geschaeftsnummer.jahr);
        }
      } else {
        return this.orderByComparator(a.namePolitischeGemeinde, b.namePolitischeGemeinde);
      }
    }
  }

  public dateComparator(a: string, b: string) {
    return this.orderByComparator(a, b);
  }

  public gemeindeComparator = (a: GbGemeindeListEntry, b: GbGemeindeListEntry) => {
    const aGemeinde = translateName(a.name, this.translateService);
    const bGemeinde = translateName(b.name, this.translateService);

    return this.orderByComparator(aGemeinde, bGemeinde);
  }

  public personStammComparator = (a: PersonStammVersionDetailEntryMeta, b: PersonStammVersionDetailEntryMeta) => {
    const format = this.config.formatConfig.getFormat('STAMMPERSON', null, null);
    if (a.name === b.name) {
      if (a.vorname === b.vorname) {
        if (format === 'STAMMPERSON_TYP01') {
          if (a.ledigname === b.ledigname) {
            return this.orderByComparator(a.geburtsdatum, b.geburtsdatum);
          } else {
            return this.orderByComparator(a.ledigname, b.ledigname);
          }
        } else {
          return 0;
        }
      } else {
        return this.orderByComparator(a.vorname, b.vorname);
      }
    } else {
      return this.orderByComparator(a.name, b.name);
    }
  }


  public personListTyp02Comparator(a: PersonStammMasterListEntryMeta[], b: PersonStammMasterListEntryMeta[]) {
    const personA = a[0];
    const personB = b[0];
    if (personA && personB) {
      if (personA.version.name === personB.version.name) {
        if (personA.version.vorname === personB.version.vorname) {
        } else {
          return this.orderByComparator(personA.version.vorname, personB.version.vorname);
        }
      } else {
        return this.orderByComparator(personA.version.name, personB.version.name);
      }
    }
  }


  public personNameVornameComparator(a: PersonNameVorname, b: PersonNameVorname) {
    if (a && b) {
      if (a.name === b.name) {
        return this.orderByComparator(a.vorname, b.vorname);
      } else {
        return this.orderByComparator(a.name, b.name);
      }
    }
  }


  public avMutationComparator(a: AvMutation, b: AvMutation) {
    if (a && b) {
      if (a.mutationNrNBIdent === b.mutationNrNBIdent) {
        if (a.mutationNrNummer === b.mutationNrNummer) {
        } else {
          return this.orderByComparator(a.mutationNrNummer, b.mutationNrNummer);
        }
      } else {
        return this.orderByComparator(a.mutationNrNBIdent, b.mutationNrNBIdent);
      }
    }
  }

  public pfandrechtBelastungComparator(aVm: RootBelastungVm, bVm: RootBelastungVm) {
    if (aVm && bVm) {
      const a = aVm.lastVersion;
      const b = bVm.lastVersion;
      if (a.pfandstelle === b.pfandstelle) {
        if (a.rangbestimmendesDatum === b.rangbestimmendesDatum) {
          if (this.belegComparator(a.beleg, b.beleg) === 0) {
            const aRechteId = aVm.pfandrechtMasterVm.object.rechteId;
            const bRechteId = bVm.pfandrechtMasterVm.object.rechteId;
            return this.rechtIdComparator(aRechteId, bRechteId);
          } else {
            return this.belegComparator(a.beleg, b.beleg);
          }
        } else {
          return this.orderByComparator(a.rangbestimmendesDatum, b.rangbestimmendesDatum);
        }
      } else {
        return this.orderByComparator(a.pfandstelle, b.pfandstelle);
      }
    }
  }

  public lastRechtRootEntryVmComparator(a: LastRechtRootEntryVm, b: LastRechtRootEntryVm) {
    if (a && b) {

      const rechtVersionA = a.standardRechtMasterVm.lastVersion.object;
      const rechtMasterA = a.standardRechtMasterVm.object;
      const rechtVersionB = b.standardRechtMasterVm.lastVersion.object;
      const rechtMasterB = b.standardRechtMasterVm.object;

      if (rechtVersionA.beginndatum === rechtVersionB.beginndatum) {
        if (this.belegComparator(rechtVersionA.beleg, rechtVersionB.beleg) === 0) {
          if (rechtMasterA.rechteId.grundbuchamt.nummer === rechtMasterB.rechteId.grundbuchamt.nummer) {
            if (rechtMasterA.rechteId.rechteIdJahr === rechtMasterB.rechteId.rechteIdJahr) {
              if (rechtMasterA.rechteId.rechteIdNummer === rechtMasterB.rechteId.rechteIdNummer) {
                if (a.belastungsArt === b.belastungsArt) {
                  return this.orderByComparator(rechtVersionA.histInfo.erstellzeitpunkt, rechtVersionB.histInfo.erstellzeitpunkt);
                } else {
                  return this.beteiligungsArtComparator(a.belastungsArt, b.belastungsArt);
                }
              } else {
                return this.orderByComparator(rechtMasterA.rechteId.rechteIdNummer, rechtMasterB.rechteId.rechteIdNummer);
              }
            } else {
              return this.orderByComparator(rechtMasterA.rechteId.rechteIdJahr, rechtMasterB.rechteId.rechteIdJahr);
            }
          } else {
            return this.orderByComparator(rechtMasterA.rechteId.grundbuchamt.nummer, rechtMasterB.rechteId.grundbuchamt.nummer);
          }
        } else {
          return this.belegComparator(rechtVersionA.beleg, rechtVersionB.beleg);
        }
      } else {
        return this.orderByComparator(rechtVersionA.beginndatum, rechtVersionB.beginndatum);
      }
    }
  }


  public lastRechtRootEntryByGsNummerComparator(a: LastRechtRootEntryVm, b: LastRechtRootEntryVm) {
    if (a && b) {
      if (a.selfReferences[0].grundstueck.grundstueckId.id === b.selfReferences[0].grundstueck.grundstueckId.id) {
        return this.rechtIdComparator(a.object.rechteId, b.object.rechteId);
      } else {
        return this.grundstueckComparator(a.selfReferences[0].grundstueck, b.selfReferences[0].grundstueck);
      }
    }
  }

  public beteiligungsArtComparator(a: LastRechtGrundstueckEntryMeta.BelastungArtEnum, b: LastRechtGrundstueckEntryMeta.BelastungArtEnum) {
    const belastet = LastRechtGrundstueckEntryMeta.BelastungArtEnum.BELASTET;
    const berechtigt = LastRechtGrundstueckEntryMeta.BelastungArtEnum.BERECHTIGT;
    const belastetBerechtigt = LastRechtGrundstueckEntryMeta.BelastungArtEnum.BELASTETBERECHTIGT;
    if (a && b) {
      if (a === b) {
        return -1;
      } else if (a === belastet && b === berechtigt) {
        return -1;
      } else if (a === belastet && b === belastetBerechtigt) {
        return -1;
      } else if (a === berechtigt && b === belastetBerechtigt) {
        return -1;
      } else {
        return 1;
      }
    }
  }


  public lastAbstractRangregelungVm(a: AbstractRangregelungVm, b: AbstractRangregelungVm) {
    if (a && b) {
      if (a.rangregelungDatum === b.rangregelungDatum) {
        if (this.belegComparator(a.lastVersion.beleg, b.lastVersion.beleg) === 0) {
          return this.orderByComparator(a.erstellzeitpunkt, b.erstellzeitpunkt);
        } else {
          return this.belegComparator(a.lastVersion.beleg, b.lastVersion.beleg);
        }
      } else {
        return this.orderByComparator(a.rangregelungDatum, b.rangregelungDatum);
      }
    }
  }

  public codeEntryComparator(a: CodeEntry, b: CodeEntry) {
    const a_text = translateName(a?.text, this.translateService);
    const b_text = translateName(b?.text, this.translateService);
    return this.orderByComparator(a_text, b_text);
  }

  public eingangadressenComparator(a: GebaeudeEingangAdresseVersionDetailEntryMeta, b: GebaeudeEingangAdresseVersionDetailEntryMeta) {
    if (a || b) {
      if (!a) {
        return 1;
      }
      if (!b) {
        return -1;
      }
      let strasseA = a?.strasse ? a?.strasse.text : a?.gebaeudeBezeichnung;
      let strasseB = b?.strasse ? b?.strasse.text : b?.gebaeudeBezeichnung;
      strasseA = strasseA ? strasseA : a.benanntesGebiet?.text;
      strasseB = strasseB ? strasseB : b.benanntesGebiet?.text;
      if (strasseA === null) {
        return 1;
      }
      if (strasseB === null) {
        return -1;
      }
      if (strasseA === strasseB) {
        if (a.hausnummer === b.hausnummer && a.histInfo && b.histInfo) {
          return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
        }
        if (a.hausnummer === null) {
          return 1;
        }
        if (b.hausnummer === null) {
          return -1;
        }
        return this.hausnummerComparator(a.hausnummer, b.hausnummer);

      }
      return this.orderByComparator(strasseA, strasseB);
    }
    return 0;
  }

  public eingangadressenListEntryListComparator = (a: Array<AvGebaeudeEingangAdresseListEntry>, b: Array<AvGebaeudeEingangAdresseListEntry>) => {
    return this.eingangadressenListEntryComparator(a[0], b[0]);
  }

  public eingangadressenListEntryComparator = (a: AvGebaeudeEingangAdresseListEntry, b: AvGebaeudeEingangAdresseListEntry) => {
    if (a && b) {
      const strasseA = a?.strasse ? a?.strasse : a?.gebaeudeBezeichnung;
      const strasseB = b?.strasse ? b?.strasse : b?.gebaeudeBezeichnung;
      if (strasseA === strasseB) {
        if (!a.hausnummer) {
          return 1;
        }
        if (!b.hausnummer) {
          return -1;
        }
        return this.hausnummerComparator(a.hausnummer, b.hausnummer);
      }
      return this.orderByComparator(strasseA, strasseB);
    }
    return 0;
  }

  public hausnummerComparator = (a: string, b: string) => {
    const regexp: RegExp = /^(\d*)(\D*)$/;
    const resultA: RegExpExecArray = regexp.exec(a);
    const resultB: RegExpExecArray = regexp.exec(b);
    if (!resultA || !resultB) {
      return this.orderByComparator(a, b);
    }
    const numbersCompared = this.orderByComparator(+resultA[1], +resultB[1]);
    if (numbersCompared !== 0) {
      return numbersCompared;
    }
    return this.orderByComparator(resultA[2], resultB[2]);
  }

  public eingangadressenMasterDetailComparator(a: GebaeudeEingangAdresseMasterDetailEntryMeta, b: GebaeudeEingangAdresseMasterDetailEntryMeta) {
    const rechtsgueltigeVersion = (entry: GebaeudeEingangAdresseMasterDetailEntryMeta) => entry.versions.find(version => version.histInfo.histEnum === HistInfo.HistEnumEnum.VONRECHTGUELTIGBISNULL && version.histInfo.bisTimePoint === null);
    return environment.deploymentMode === "PUBLIC" ?
      this.eingangadressenComparator(rechtsgueltigeVersion(a), rechtsgueltigeVersion(b)) : //
      this.eingangadressenComparator(a.versions.find(version => version.histInfo.bisTimePoint === null), b.versions.find(version => version.histInfo.bisTimePoint === null));
  }

  public eigentumanteilMasterComparator(a: EigentumanteilMasterDetailEntryMeta, b: EigentumanteilMasterDetailEntryMeta) {
    if (a && b) {
      if (a.eigentumanteilArt === b.eigentumanteilArt) {
        if (a.eigentumanteilArt === 'GRUNDSTUECK' && b.eigentumanteilArt === 'GRUNDSTUECK') {
          return this.grundstueckComparator(a.berechtigtesGrundstueck.grundstueck, b.berechtigtesGrundstueck.grundstueck);
        }
      }
      return this.erstellzeitpunktComparator(a, b);
    }
  }

  public eigentumanteilVersionComparator(a: EigentumanteilVersionDetailEntryMeta, b: EigentumanteilVersionDetailEntryMeta) {
    if (a && b) {
      return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
    }
  }

  public erstellzeitpunktComparator(a: any, b: any) {
    if (a && b && a.hasOwnProperty('histInfo') && b.hasOwnProperty('histInfo')) {
      return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
    }
    return 0;
  }


  public personGbMasterComparator(a: PersonGbMasterDetailEntryMeta, b: PersonGbMasterDetailEntryMeta) {
    if (a && b) {
      const versionA = a.versions[a.versions.length - 1];
      const versionB = b.versions[b.versions.length - 1];
      if (versionA === null) {
        return 1;
      }
      if (versionB === null) {
        return -1;
      }
      if (versionA.name === versionB.name) {
        if (versionA.vorname === versionB.vorname) {
        } else {
          return this.orderByComparator(versionA.vorname, versionB.vorname);
        }
      } else {
        return this.orderByComparator(versionA.name, versionB.name);
      }
      return this.orderByComparator(versionA.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);

    }
    return 0;
  }

  public flaechenanteileComparator(a: GrundstueckFlaechenanteilMasterDetailEntry, b: GrundstueckFlaechenanteilMasterDetailEntry, grundstueckId: GrundstueckId) {
    const aBelastet = a.sdrGrundstueck && a.sdrGrundstueck.grundstueckId.id === grundstueckId.id;
    const bBelastet = b.sdrGrundstueck && b.sdrGrundstueck.grundstueckId.id === grundstueckId.id;
    if (a && b) {
      if (aBelastet && bBelastet) {
        return this.grundstueckComparator(a.grundstueck, b.grundstueck);
      } else if (!aBelastet && !bBelastet) {
        return this.grundstueckComparator(a.sdrGrundstueck, b.sdrGrundstueck);
      }
      if (aBelastet && !bBelastet) {
        return -1;
      } else {
        return 1;
      }
    }
  }

  public avBemerkungMasterDetailEntryComparator(a: AvBemerkungMasterDetailEntry, b: AvBemerkungMasterDetailEntry): number {
    if (a && b) {
      const artA = a.versions[a.versions.length - 1].art !== AvBemerkungVersionDetailEntry.ArtEnum.ANDERE ? a.versions[a.versions.length - 1].art.toString() : a.versions[a.versions.length - 1].andereArt;
      const artB = b.versions[b.versions.length - 1].art !== AvBemerkungVersionDetailEntry.ArtEnum.ANDERE ? b.versions[b.versions.length - 1].art.toString() : b.versions[b.versions.length - 1].andereArt;
      if (artA === artB) {
        const bemerkungA = a.versions[a.versions.length - 1].bemerkung;
        const bemerkungB = b.versions[b.versions.length - 1].bemerkung;
        return this.orderByComparator(bemerkungA, bemerkungB);
      } else {
        return this.orderByComparator(artA, artB);
      }
    } else {
      return this.orderByComparator(a, b);
    }
  }

  public bodenbedeckungComparator = (a: BodenbedeckungMasterDetailEntry, b: BodenbedeckungMasterDetailEntry) => {
    const versionsA: BodenbedeckungVersionDetailEntry[] = a.versions.sort(this.bodenbedeckungVersionDetailEntryComparator);
    const versionsB: BodenbedeckungVersionDetailEntry[] = b.versions.sort(this.bodenbedeckungVersionDetailEntryComparator);
    const versionA = versionsA[versionsA.length - 1];
    const versionB = versionsB[versionsB.length - 1];
    if (versionA.codeBodenbedeckungArt && versionB.codeBodenbedeckungArt) {
      return this.orderByComparator(translateName(versionA.codeBodenbedeckungArt.text, this.translateService),
        (translateName(versionB.codeBodenbedeckungArt.text, this.translateService)));
    }
    if (versionA.codeBodenbedeckungArt) {
      return -1;
    }
    if (versionB.codeBodenbedeckungArt) {
      return 1;
    }
    return this.orderByComparator(versionA.sortIndex, versionB.sortIndex);
  }

  public bodenbedeckungVersionDetailEntryComparator = (a: BodenbedeckungVersionDetailEntry, b: BodenbedeckungVersionDetailEntry) => {
    return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
  }

  public gbplanMasterDetailEntryComparator(a: GbplanMasterDetailEntry, b: GbplanMasterDetailEntry) {
    if (a && b && a.versions && b.versions) {
      const planNummerA = a.versions[a.versions.length - 1].planNummer;
      const planNummerB = b.versions[b.versions.length - 1].planNummer;
      return this.orderByComparator(planNummerA, planNummerB);
    } else {
      return this.orderByComparator(a, b);
    }
  }

  public grundstueckadresseDetailEntryComparator(a: GrundstueckadresseDetailEntry, b: GrundstueckadresseDetailEntry) {
    if (a && b) {
      const adressTextA = a.adressText;
      const adressTextB = b.adressText;
      return this.orderByComparator(adressTextA, adressTextB);
    } else {
      return this.orderByComparator(a, b);
    }
  }

  public lagebezeichnungComparator(a: GrundstueckadresseDetailEntry, b: GrundstueckadresseDetailEntry, unbekanntValue: GrundstueckadresseDetailEntry.LagebezeichnungsTypEnum, isErweiterungAvPlusActive: boolean) {
    const typeA = isErweiterungAvPlusActive ? a.lagebezeichnungsTyp : unbekanntValue;
    const typeB = isErweiterungAvPlusActive ? b.lagebezeichnungsTyp : unbekanntValue;
    if (typeA !== typeB) {
      return this.orderByComparator(ComparatorService.getLagebezeichnungTypSortOrder(typeA), ComparatorService.getLagebezeichnungTypSortOrder(typeB));
    }
    const nameComparison = this.orderByComparator(a.adressText, b.adressText);
    if (nameComparison === 0) {
      return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
    }
    return nameComparison;
  }

  public pfandrechtBelatungMasterComparator(a: PfandrechtBelastungMasterDetailEntryMeta, b: PfandrechtBelastungMasterDetailEntryMeta) {
    if (a && b) {
      return this.grundstueckComparator(a.grundstueck, b.grundstueck);
    }
  }

  public glaeubigerSchuldnerMasterComparator(a: GlaeubigerMasterDetailEntryMeta | SchuldnerMasterDetailEntryMeta, b: GlaeubigerMasterDetailEntryMeta | SchuldnerMasterDetailEntryMeta) {
    if (a && b) {
      return this.personGbListEntryComparator(a.versions[a.versions.length - 1].personGb, b.versions[b.versions.length - 1].personGb);
    }
  }

  public personGbListEntryComparator(a: PersonGbListEntryMeta, b: PersonGbListEntryMeta) {
    if (a && b) {
      if (a.name === b.name) {
        if (a.vorname === b.vorname) {
        } else {
          return this.orderByComparator(a.vorname, b.vorname);
        }
      } else {
        return this.orderByComparator(a.name, b.name);
      }
      return this.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt);
    }
  }

  public stockwerkComparator(a: Stockwerk, b: Stockwerk) {
    return this.orderByComparator(a.stockwerkNummer + a.stockwerkBezeichnung, b.stockwerkNummer + b.stockwerkBezeichnung);
  }

  public gebaeudeEingangAdresseComparator(a: GebaeudeEingangAdresseListEntry, b: GebaeudeEingangAdresseListEntry) {
    const aName = translateName(a.strasse, this.translateService);
    const bName = translateName(b.strasse, this.translateService);
    if (aName === bName) {
      if (a.hausnummer === b.hausnummer) {
        if (a.plz === b.plz) {
          if (a.plzZusatz === b.plzZusatz) {
            return this.orderByComparator(a.ortschaft, b.ortschaft);
          }
          return this.orderByComparator(a.plzZusatz, b.plzZusatz);
        }
        return this.orderByComparator(a.plz, b.plz);
      }
      return this.orderByComparator(a.hausnummer, b.hausnummer);
    }
    return this.orderByComparator(aName, bName);
  }

  public propertyComparator(property: string): (a: any, b: any) => number {
    return (a: any, b: any) => {
      if (a && b && a.hasOwnProperty(property) && b.hasOwnProperty(property)) {
        return this.orderByComparator(a[property], b[property]);
      }
      return 0;
    };
  }

}
